import React, {Component} from 'react'
import {connect} from 'react-redux'
import propTypes from 'prop-types'
import {get, isEmpty, isNil} from 'lodash'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Button from 'components/button'
import NumberString from 'containers/numberString'
import Translator from "components/translator"
import Dialog from 'components/dialog'
import DialogMessage from 'components/dialogMessage'
import ProgressBar from 'components/progressBar'
import Prompt from 'components/prompt'
import {exportData} from 'actions/pivotTable'
import {getAllSelectedRows} from 'selectors/pivotTable'
import {trackEvent} from 'helpers/tracking'
import {t} from "helpers/language"
import {getTitleForTotalItems} from 'helpers/string'
import './pivotTableExporter.scss'

const checkboxStyle = {width: 60, height: 20}
const fieldLabelStyle = {marginLeft: 0}

class PivotTableExporter extends Component {
  constructor(props) {
    super(props)
    this.state = {
      exportingColumns: [],
      isColumnsEmptyPromptVisible: false,
    }
  }

  componentDidUpdate(prevProps) {
    const {active, defaultColumns} = this.props
    const {exportingColumns} = this.state

    const isActiveChanged = !prevProps.active && active && isEmpty(exportingColumns)
    const isDefaultColumnsChanged =
      JSON.stringify(prevProps.defaultColumns) !== JSON.stringify(defaultColumns)
    if (isActiveChanged || isDefaultColumnsChanged) {
      this.setState({
        exportingColumns: isEmpty(defaultColumns) ? [] : defaultColumns,
      })
    }
  }

  toggleExportingColumn = (key, checked) => {
    const {exportingColumns} = this.state
    const updatedExportingColumns = checked
      ? [...exportingColumns, key]
      : exportingColumns.filter(c => c !== key)

    this.setState({exportingColumns: updatedExportingColumns})
  }

  toggleAllExportingColumns = (checked) => {
    const {allColumns} = this.props
    const updatedExportingColumns = checked
      ? Object.keys(allColumns)
      : []

    this.setState({exportingColumns: updatedExportingColumns})
  }

  sortColumns = (colA, colB) => {
    const labelA = colA.label || colA.alias
    const labelB = colB.label || colB.alias
    if (labelA > labelB) return 1
    if (labelA < labelB) return -1
    return 0
  }

  getColumnData = (column) => {
    const exportData = get(column, 'export', null)
    const data = get(column, 'data', null)
    return {
      ...column,
      data: exportData || data,
    }
  }

  onExport = () => {
    trackEvent('Export Dialog', 'Clicked on export', 'Export')
    const {allColumns, defaultColumns, onExport, onClose} = this.props
    const {exportingColumns} = this.state

    const defaultSelectedKeys = isEmpty(defaultColumns)
      ? []
      : defaultColumns
        .filter(key => exportingColumns.includes(key))
        .map(key => allColumns[key])

    const selectedKeys = exportingColumns
      .filter(key => !defaultColumns.includes(key))
      .map(key => allColumns[key])
      .sort(this.sortColumns)

    const columns = [...defaultSelectedKeys, ...selectedKeys]
      .filter(column => !isNil(column))
      .map(this.getColumnData)

    if (!isEmpty(columns)) {
      onClose()
      onExport(columns)
    }
  }

  getTotalRecords = () => {
    const {totalRecords, selectedRows} = this.props
    return isEmpty(selectedRows) ? totalRecords : selectedRows.length
  }

  onCancelColumnsEmptyPrompt = () => {
    this.setState({isColumnsEmptyPromptVisible: false})
  }

  getProgress = () => {
    const {exportingRows, exportedRows, pluralNoun, exportNoun} = this.props

    const isExporting = exportedRows && exportingRows
    const progress = isExporting ? exportedRows / exportingRows * 100 : 0
    const noun = exportNoun || pluralNoun

    let status = 'Please Wait...'
    if (isExporting) {
      const exportedRowsNumberString = <NumberString>{exportedRows}</NumberString>
      const exportingRowsNumberString = <NumberString>{exportingRows}</NumberString>

      status = progress >= 100
        ? 'Writing file (this may take several minutes)...'
        : (
          <div>
            Processing {exportedRowsNumberString} of {exportingRowsNumberString} {noun.toLowerCase()}
          </div>
        )
    }

    return (
      <ProgressBar
        value={progress}
        label={status}
        width="100%"/>
    )
  }

  getAllSortedColumns = () => {
    const {allColumns} = this.props
    return Object
      .values(allColumns)
      .sort(this.sortColumns)
  }

  getSelectedColumns = () => {
    const {exportingColumns} = this.state
    return this.getAllSortedColumns()
      .filter(column => exportingColumns.includes(get(column, 'key', null)))
  }

  getNonSelectedColumns = () => {
    const {exportingColumns} = this.state
    return this.getAllSortedColumns()
      .filter(column => !exportingColumns.includes(get(column, 'key', null)))
  }

  render() {
    const {active, allColumns, singularNoun, pluralNoun, exportNoun, exporting, onClose} = this.props
    const {exportingColumns, isColumnsEmptyPromptVisible} = this.state

    const selectedColumns = this.getSelectedColumns()
    const unselectedColumns = this.getNonSelectedColumns()
    const totalRecords = this.getTotalRecords()

    const noun = exportNoun || pluralNoun

    return (
      <div>
        <Prompt
          key={'pivot-table-exporting-prompt'}
          className={'pivot-table-exporting-prompt'}
          open={exporting}
          title={`Exporting ${noun}`}>
          {this.getProgress()}
        </Prompt>
        <Dialog
          actions={[
            <Button
              flat={true}
              className={'cancel-export-button'}
              key={'cancel-export-button'}
              label='Cancel'
              style={{marginRight: 24}}
              onClick={onClose}
            />,
            <Button
              flat={true}
              className={'export-button'}
              key={'export-button'}
              label='Export'
              primary={true}
              onClick={this.onExport}
            />,
          ]}
          className={'pivot-table-exporter-container'}
          onRequestClose={onClose}
          showHeader={true}
          title={t(`Export ${noun}`)}
          visible={active}
        >

          <DialogMessage show={!isNil(totalRecords)}>
            {exportNoun
              ? t(`This will export the ${exportNoun} to a CSV (comma-separated file)`)
              : (
                <Translator
                  onRender={({singularNoun, pluralNoun}) => {
                    const titleForTotalItems = getTitleForTotalItems(totalRecords, singularNoun, pluralNoun)
                    return t(`This will export {1} to a CSV (comma-separated file)`, titleForTotalItems)
                  }}
                  text={{singularNoun, pluralNoun}}
                />
              )
            }
          </DialogMessage>

          <div>{t('Choose the data you would like to export:')}</div>
          <div className={'select-all-fields-wrapper'}>
            <FormControlLabel
              className={'select-deselect-fields'}
              style={fieldLabelStyle}
              control={
                <Checkbox
                  style={checkboxStyle}
                  checked={exportingColumns.length === Object.keys(allColumns).length}
                  onChange={(e, v) => this.toggleAllExportingColumns(v)}
                />
              }
              label={t("Select / Deselect All Fields")}/>
          </div>
          <div className={'checkbox-title'}>{t('Selected Fields')}</div>
          {selectedColumns.map(column => (
            <div key={column.key}>
              <FormControlLabel
                style={fieldLabelStyle}
                control={
                  <Checkbox
                    style={checkboxStyle}
                    checked={true}
                    onChange={(e, v) => this.toggleExportingColumn(column.key, v)}
                  />
                }
                label={column.label || column.alias}
              />
            </div>
          ))}
          <div className={'checkbox-title fields-section-separator'}>
            {t('Available Fields')}
          </div>
          {unselectedColumns.map(column => (
            <div key={column.key}>
              <FormControlLabel
                style={fieldLabelStyle}
                control={
                  <Checkbox
                    style={checkboxStyle}
                    checked={false}
                    onChange={(e, v) => this.toggleExportingColumn(column.key, v)}
                  />
                }
                label={column.label || column.alias}
              />
            </div>
          ))}
        </Dialog>
        <Prompt
          key={'pivot-table-columns-prompt'}
          className={'no-columns-selected-prompt'}
          open={isColumnsEmptyPromptVisible}
          title={`Export ${noun}`}
          actions={[{
            title: 'Close',
            onAction: this.onCancelColumnsEmptyPrompt,
          }]}
        >
          To export this data select at least one field from the Available Fields list
        </Prompt>
      </div>
    )
  }
}

PivotTableExporter.propTypes = {
  stateKey: propTypes.string.isRequired,
  active: propTypes.bool.isRequired,
  allColumns: propTypes.object.isRequired,
  defaultColumns: propTypes.array,
  singularNoun: propTypes.string.isRequired,
  pluralNoun: propTypes.string.isRequired,
  exportNoun: propTypes.string,
  fileName: propTypes.string,
  totalRecords: propTypes.number.isRequired,
  selectedRows: propTypes.array.isRequired,
  params: propTypes.object,
  onClose: propTypes.func.isRequired,
  exporting: propTypes.bool,
  exportingRows: propTypes.number,
  exportedRows: propTypes.number,
  onExport: propTypes.func.isRequired,
}

PivotTableExporter.defaultProps = {
  exporting: false,
  defaultColumns: [],
}

const mapStateToProps = (state, ownProps) => {
  const pivotState = get(state, ownProps.stateKey, {})
  const {exporting, exportingRows, exportedRows} = pivotState
  return {
    exporting,
    exportingRows,
    exportedRows,
    selectedRows: getAllSelectedRows(state, ownProps.stateKey),
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const fileName = ownProps.fileName || ownProps.exportNoun || ownProps.pluralNoun
  return {
    onExport: (columns) => dispatch(exportData(ownProps.stateKey, columns, fileName, ownProps.params)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PivotTableExporter)
